import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import {
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  CalendarOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";

// Selector de fecha con scroll continuo tipo "página": el mes se desliza y
// cambia con el scroll nativo del mouse/trackpad (scroll-snap), en vez de
// saltar de golpe al usar botones. No depende del DatePicker de antd.
//
// Soporta además los patrones ya usados en el sistema para integrarse en
// formularios existentes:
//  - `open`/`onOpenChange` controlados: para el patrón de overlay invisible
//    (un <Input> visible con máscara + este picker transparente encima o al
//    lado, abierto por un ícono de calendario externo).
//  - `showTime`: agrega hora/minuto al panel (sin botón de confirmar, igual
//    que el uso existente de `needConfirm={false}`).
//  - `allowClear`, `disabled`: paridad básica con antd.

const WEEKDAYS_ES = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];
const MONTHS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];
const PANEL_HEIGHT = 260; // alto fijo de la grilla (6 filas), igual para todos los meses

const buildFixedMonthGrid = (monthStart) => {
  // Lunes de la semana que contiene monthStart. Calculado a partir de
  // .day() (0=domingo..6=sábado, siempre así sin importar el locale activo)
  // en vez de .startOf("week"), porque ese método SÍ depende del locale
  // (con "es" ya arranca en lunes) y sumarle +1 día duplicaba el corrimiento.
  const gridStart = monthStart.subtract((monthStart.day() + 6) % 7, "day");
  const days = [];
  for (let i = 0; i < 42; i++) days.push(gridStart.add(i, "day")); // siempre 6 filas
  const rows = [];
  for (let i = 0; i < 42; i += 7) rows.push(days.slice(i, i + 7));
  return rows;
};

const pad2 = (n) => String(n).padStart(2, "0");

// Máscara de escritura manual: el usuario solo teclea dígitos, acá se
// insertan las "/" (y " " + ":" con hora) progresivamente, tipo input
// bancario. `withTime` agrega HH:mm al final (12 dígitos en vez de 8).
const buildMaskedText = (digits, withTime) => {
  const d = digits.slice(0, withTime ? 12 : 8);
  let out = "";
  if (d.length > 0) out += d.slice(0, 2);
  if (d.length > 2) out += "/" + d.slice(2, 4);
  if (d.length > 4) out += "/" + d.slice(4, 8);
  if (withTime && d.length > 8) out += " " + d.slice(8, 10);
  if (withTime && d.length > 10) out += ":" + d.slice(10, 12);
  return out;
};

// Convierte el texto tecleado (ya con máscara) a dayjs. Devuelve null si
// aún está incompleto o la fecha/hora no es válida (p.ej. 31/02).
const parseTypedText = (text, withTime) => {
  const digits = String(text || "").replace(/\D/g, "");
  if (withTime) {
    if (digits.length < 12) return null;
    const [dd, mm, yyyy, hh, mi] = [
      digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8), digits.slice(8, 10), digits.slice(10, 12),
    ];
    const d = dayjs(`${yyyy}-${mm}-${dd} ${hh}:${mi}`, "YYYY-MM-DD HH:mm", true);
    return d.isValid() ? d : null;
  }
  if (digits.length < 8) return null;
  const [dd, mm, yyyy] = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
  const d = dayjs(`${yyyy}-${mm}-${dd}`, "YYYY-MM-DD", true);
  return d.isValid() ? d : null;
};

// antd acepta `format` como string ("DD/MM/YYYY") o como función
// (v) => string para formatos personalizados (p.ej. "07 DE SEPTIEMBRE DE
// 2026"). Varios formularios ya usaban esa segunda forma con su DatePicker.
const formatValue = (v, fmt) => {
  if (!v) return "";
  if (typeof fmt === "function") return fmt(v);
  return v.format(fmt);
};

const ScrollDatePicker = React.forwardRef(function ScrollDatePicker(
  {
    value,
    onChange,
    format = "DD/MM/YYYY",
    className,
    style,
    popupStyle,
    placeholder,
    autoFocus = false,
    open: openProp,
    onOpenChange,
    allowClear = false,
    disabled = false,
    showTime = false,
    minuteStep = 1,
    needConfirm,
    onKeyDown,
  },
  ref
) {
  const isOpenControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpenControlled ? !!openProp : internalOpen;

  // Con hora activa, antd requiere confirmar con "Aceptar" antes de disparar
  // onChange (y por lo tanto antes de cerrar), salvo que el llamador pase
  // needConfirm={false} explícitamente (igual semántica que antd v5).
  const needsConfirm = showTime && needConfirm !== false;

  const [monthCursor, setMonthCursor] = useState(() => (value || dayjs()).startOf("month"));
  // "day": grilla de días (normal) | "month": grilla de meses del año | "year": grilla
  // de años (bloque de 12) — permite saltar directo a un mes/año lejano (p.ej. fechas
  // atrasadas) en vez de recorrer mes por mes con las flechas.
  const [view, setView] = useState("day");
  const [coords, setCoords] = useState(null);
  const [pendingTime, setPendingTime] = useState(() => ({
    h: value ? value.hour() : 0,
    m: value ? value.minute() : 0,
  }));
  const [draft, setDraft] = useState(value || null);

  // Escritura manual: mientras el usuario teclea, el input muestra
  // `typedText` (máscara dd/mm/yyyy[ hh:mm]) en vez del valor formateado
  // con `format` — así se puede escribir la fecha a mano, igual que un
  // <input type="date"> nativo, sin depender solo del calendario.
  const [isTyping, setIsTyping] = useState(false);
  const [typedText, setTypedText] = useState("");

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const scrollRef = useRef(null);
  const settleTimerRef = useRef(null);
  const recyclingRef = useRef(false);
  const recyclingUnlockRef = useRef(null);
  const wasOpenRef = useRef(open);
  const [snapDisabled, setSnapDisabled] = useState(false);

  // Paridad con antd: exponer .focus() para integraciones que hacen
  // ref.current.focus() (p.ej. navegación con Enter entre campos).
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }), []);

  const today = useMemo(() => dayjs().startOf("day"), []);
  const selected = (needsConfirm ? draft : value) ? (needsConfirm ? draft : value).startOf("day") : null;

  const months = useMemo(
    () => [monthCursor.subtract(1, "month"), monthCursor, monthCursor.add(1, "month")],
    [monthCursor]
  );

  const positionPanel = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 280) });
  }, []);

  const setOpenState = useCallback(
    (next) => {
      if (!isOpenControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isOpenControlled, onOpenChange]
  );

  const closePanel = useCallback(() => setOpenState(false), [setOpenState]);
  const toggle = () => {
    if (disabled) return;
    setOpenState(!open);
  };

  useEffect(() => {
    if (autoFocus) {
      setOpenState(true);
      inputRef.current?.focus();
    }
    // Solo al montar (equivalente al autoFocus original de antd).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al abrir (manual o vía prop controlada externa): centrar el panel en el
  // mes del valor actual y arrancar el reloj desde la hora ya seleccionada.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setMonthCursor((value || dayjs()).startOf("month"));
      setPendingTime({ h: value ? value.hour() : 0, m: value ? value.minute() : 0 });
      setDraft(value || null);
      setView("day");
      positionPanel();
    }
    wasOpenRef.current = open;
  }, [open, value, positionPanel]);

  // Centra el scroll en el panel del medio: al abrir y cada vez que se
  // "recicla" el mes actual tras un settle de scroll (sin animación, para
  // que el reciclado sea invisible).
  //
  // El scroll-snap nativo del navegador puede seguir animando (inercia de
  // trackpad) cuando reciclamos scrollTop a mano: si no lo apagamos, esa
  // animación en vuelo sigue moviendo el scroll después del reciclado y
  // dispara otro settle "fantasma", saltando 2 meses en vez de 1 con un
  // scroll suave. Se apaga snap, se resetea, y se reactiva ya asentado.
  useLayoutEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;

    recyclingRef.current = true;
    setSnapDisabled(true);
    el.scrollTop = PANEL_HEIGHT;

    clearTimeout(recyclingUnlockRef.current);
    recyclingUnlockRef.current = setTimeout(() => {
      recyclingRef.current = false;
      setSnapDisabled(false);
    }, 120);

    return () => clearTimeout(recyclingUnlockRef.current);
  }, [open, monthCursor]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      closePanel();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closePanel();
    };
    const onReflow = () => positionPanel();

    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);

    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, closePanel, positionPanel]);

  const settleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || recyclingRef.current) return;

    const index = Math.round(el.scrollTop / PANEL_HEIGHT);
    if (index === 1) return;

    const delta = index - 1; // -1 mes anterior, +1 mes siguiente
    setMonthCursor((m) => m.add(delta, "month"));
  }, []);

  const onScroll = useCallback(() => {
    if (recyclingRef.current) return;
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(settleScroll, 90);
  }, [settleScroll]);

  useEffect(() => () => clearTimeout(settleTimerRef.current), []);

  const goMonth = useCallback((dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: PANEL_HEIGHT * (1 + dir), behavior: "smooth" });
  }, []);

  // Bloque de 12 años mostrado en la vista "year" (p.ej. 2020–2031).
  const yearBlockStart = Math.floor(monthCursor.year() / 12) * 12;
  const yearOptions = useMemo(
    () => Array.from({ length: 12 }, (_, i) => yearBlockStart + i),
    [yearBlockStart]
  );

  const goHeader = (dir) => {
    if (view === "day") { goMonth(dir); return; }
    if (view === "month") { setMonthCursor((m) => m.add(dir, "year")); return; }
    setMonthCursor((m) => m.add(dir * 12, "year"));
  };

  const cycleView = () => {
    setView((v) => (v === "day" ? "month" : v === "month" ? "year" : "day"));
  };

  const pickMonthOption = (idx) => {
    setMonthCursor((m) => m.month(idx).startOf("month"));
    setView("day");
  };

  const pickYearOption = (y) => {
    setMonthCursor((m) => m.year(y));
    setView("month");
  };

  const pickDay = (d) => {
    const withTime = showTime
      ? d.hour(pendingTime.h).minute(pendingTime.m).second(0).millisecond(0)
      : d.startOf("day");

    if (needsConfirm) {
      setDraft(withTime);
      return; // se confirma con el botón "Aceptar"
    }

    onChange?.(withTime);
    // Con hora activa (needConfirm={false}) dejamos el panel abierto para
    // poder ajustar hora/minuto, igual que el uso existente en el sistema.
    if (!showTime) closePanel();
  };

  const pickToday = () => {
    const withTime = showTime
      ? today.hour(pendingTime.h).minute(pendingTime.m).second(0).millisecond(0)
      : today;

    if (needsConfirm) {
      setDraft(withTime);
      return;
    }

    onChange?.(withTime);
    if (!showTime) closePanel();
  };

  const onTimeChange = (next) => {
    setPendingTime(next);
    const base = needsConfirm ? draft || value : value;
    if (!base) return;
    const merged = base.hour(next.h).minute(next.m).second(0).millisecond(0);
    if (needsConfirm) setDraft(merged);
    else onChange?.(merged);
  };

  const onAccept = () => {
    onChange?.(draft || value || null);
    closePanel();
  };

  const onClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const editFormat = showTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY";

  const handleInputFocus = () => {
    if (disabled) return;
    const base = needsConfirm ? draft || value : value;
    setTypedText(base ? base.format(editFormat) : "");
    setIsTyping(true);
    setOpenState(true);
  };

  const handleInputChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const masked = buildMaskedText(digits, !!showTime);
    setTypedText(masked);

    // En cuanto el día/mes/año quedan completos, mueve el calendario a ese
    // mes aunque falte la hora, para que el usuario vea dónde va a caer.
    if (digits.length >= 8) {
      const d = dayjs(`${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`, "YYYY-MM-DD", true);
      if (d.isValid()) setMonthCursor(d.startOf("month"));
    }
  };

  const commitTypedText = () => {
    const parsed = parseTypedText(typedText, !!showTime);
    if (parsed) {
      if (needsConfirm) setDraft(parsed);
      else onChange?.(parsed);
    } else if (typedText.trim() === "" && allowClear) {
      onChange?.(null);
    }
    // Texto inválido/incompleto: se descarta en silencio, el input vuelve a
    // mostrar el valor formateado vigente al salir del modo edición.
    setIsTyping(false);
  };

  const handleInputBlur = () => {
    if (!isTyping) return;
    commitTypedText();
  };

  const handleInputKeyDown = (e) => {
    onKeyDown?.(e);
    if (disabled) return;
    if (e.key === "Enter") {
      if (!e.defaultPrevented) e.preventDefault();
      commitTypedText();
      closePanel();
    } else if (e.key === "Escape") {
      setIsTyping(false);
    }
  };

  const minuteOptions = useMemo(() => {
    const step = Math.max(1, minuteStep || 1);
    const opts = [];
    for (let m = 0; m < 60; m += step) opts.push(m);
    return opts;
  }, [minuteStep]);

  const showClear = allowClear && !disabled && !!value;

  return (
    <div
      className={`ol-sdp${className ? ` ${className}` : ""}${disabled ? " is-disabled" : ""}`}
      style={style}
      ref={rootRef}
    >
      <div className="ol-sdp-trigger" ref={triggerRef}>
        <input
          type="text"
          inputMode="numeric"
          className="ol-sdp-input"
          ref={inputRef}
          value={isTyping ? typedText : value ? formatValue(value, format) : ""}
          placeholder={placeholder || (showTime ? "dd/mm/yyyy hh:mm" : "dd/mm/yyyy")}
          disabled={disabled}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
        />
        {showClear ? (
          <CloseCircleFilled className="ol-sdp-clear" onClick={onClear} />
        ) : (
          <CalendarOutlined
            className="ol-sdp-calIcon"
            onClick={() => {
              if (disabled) return;
              toggle();
            }}
          />
        )}
      </div>

      {open &&
        coords &&
        createPortal(
          <div
            className="ol-sdp-panel"
            ref={panelRef}
            style={{ top: coords.top, left: coords.left, minWidth: coords.width, ...popupStyle }}
          >
            <div className="ol-sdp-header">
              <button
                type="button"
                onClick={() => goHeader(-1)}
                aria-label={view === "day" ? "Mes anterior" : view === "month" ? "Año anterior" : "Años anteriores"}
              >
                <LeftOutlined />
              </button>
              <button
                type="button"
                className="ol-sdp-headerLabel"
                onClick={cycleView}
                title="Elegir mes/año directamente"
              >
                {view === "day"
                  ? monthCursor.format("MMMM YYYY")
                  : view === "month"
                  ? monthCursor.format("YYYY")
                  : `${yearBlockStart} – ${yearBlockStart + 11}`}
                <DownOutlined className="ol-sdp-headerCaret" />
              </button>
              <button
                type="button"
                onClick={() => goHeader(1)}
                aria-label={view === "day" ? "Mes siguiente" : view === "month" ? "Año siguiente" : "Años siguientes"}
              >
                <RightOutlined />
              </button>
            </div>

            {view === "day" && (
              <div className="ol-sdp-weekRow">
                {WEEKDAYS_ES.map((w) => (
                  <span key={w}>{w}</span>
                ))}
              </div>
            )}

            {view === "day" && (
              <div
                className="ol-sdp-scroll"
                ref={scrollRef}
                onScroll={onScroll}
                style={{
                  height: PANEL_HEIGHT,
                  scrollSnapType: snapDisabled ? "none" : "y mandatory",
                }}
              >
                {months.map((m) => (
                  <div className="ol-sdp-page" key={m.format("YYYY-MM")} style={{ height: PANEL_HEIGHT }}>
                    {buildFixedMonthGrid(m).map((row, ri) => (
                      <div className="ol-sdp-row" key={ri}>
                        {row.map((d) => {
                          const inMonth = d.month() === m.month();
                          const isToday = d.isSame(today, "day");
                          const isSelected = !!selected && d.isSame(selected, "day");
                          return (
                            <button
                              type="button"
                              key={d.format("YYYY-MM-DD")}
                              className={[
                                "ol-sdp-day",
                                !inMonth ? "is-muted" : "",
                                isToday ? "is-today" : "",
                                isSelected ? "is-selected" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => pickDay(d)}
                            >
                              {d.date()}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {view === "month" && (
              <div className="ol-sdp-scroll" style={{ height: PANEL_HEIGHT }}>
                <div className="ol-smp-grid">
                  {MONTHS_ES.map((label, idx) => {
                    const isToday = today.year() === monthCursor.year() && today.month() === idx;
                    const isSelected = !!selected && selected.year() === monthCursor.year() && selected.month() === idx;
                    return (
                      <button
                        type="button"
                        key={label}
                        className={["ol-smp-month", isToday ? "is-today" : "", isSelected ? "is-selected" : ""]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => pickMonthOption(idx)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {view === "year" && (
              <div className="ol-sdp-scroll" style={{ height: PANEL_HEIGHT }}>
                <div className="ol-smp-grid">
                  {yearOptions.map((y) => {
                    const isToday = today.year() === y;
                    const isSelected = !!selected && selected.year() === y;
                    return (
                      <button
                        type="button"
                        key={y}
                        className={["ol-smp-month", isToday ? "is-today" : "", isSelected ? "is-selected" : ""]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => pickYearOption(y)}
                      >
                        {y}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {view === "day" && showTime ? (
              <div className="ol-sdp-timeRow">
                <select
                  aria-label="Hora"
                  value={pendingTime.h}
                  onChange={(e) => onTimeChange({ ...pendingTime, h: Number(e.target.value) })}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {pad2(h)}
                    </option>
                  ))}
                </select>
                <span className="ol-sdp-timeSep">:</span>
                <select
                  aria-label="Minuto"
                  value={pendingTime.m}
                  onChange={(e) => onTimeChange({ ...pendingTime, m: Number(e.target.value) })}
                >
                  {minuteOptions.map((m) => (
                    <option key={m} value={m}>
                      {pad2(m)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="ol-sdp-footer">
              {view === "day" ? (
                <button type="button" onClick={pickToday}>
                  Hoy
                </button>
              ) : (
                <button type="button" onClick={() => setView("day")}>
                  Volver al calendario
                </button>
              )}
              {view === "day" && needsConfirm ? (
                <button type="button" className="ol-sdp-accept" onClick={onAccept}>
                  Aceptar
                </button>
              ) : null}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
});

export default ScrollDatePicker;
